import type { App } from "@dream/api/app";
import { TEAM_EMAIL_DOMAIN, withGoogleHostedDomainChooser } from "@dream/config/constants";
import { treaty } from "@elysiajs/eden";
import { resolveApiBaseUrl } from "./base-url.ts";

/**
 * Cliente da API.
 *
 * O Eden Treaty deriva os tipos direto do tipo `App` do Elysia, então uma
 * mudança de rota ou de schema no servidor vira erro de compilação aqui — é o
 * "end-to-end type-safe" que o briefing exige, sem geração de código.
 *
 * `credentials: "include"` é obrigatório: a sessão administrativa vive num
 * cookie `HttpOnly`, que o navegador só envia com essa opção.
 */

const BASE_URL = resolveApiBaseUrl(
  import.meta.env.VITE_API_URL as string | undefined,
  window.location.origin,
);

export const api = treaty<App>(BASE_URL, {
  fetch: { credentials: "include" },
});

export interface ApiErrorPayload {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

/**
 * Erro de API com o payload de domínio preservado.
 *
 * O código e os detalhes importam para a UI: `VERSION_CONFLICT` dispara
 * rollback visual, e `details.currentVersion` permite recarregar o estado certo
 * em vez de descartar o que o usuário fez.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown> | undefined;

  constructor(status: number, payload: ApiErrorPayload) {
    super(payload.message);
    this.name = "ApiError";
    this.status = status;
    this.code = payload.code;
    this.details = payload.details;
  }

  get isVersionConflict(): boolean {
    return this.code === "VERSION_CONFLICT" || this.status === 409;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  /** 410: link revogado ou expirado. O portal precisa distinguir de 404. */
  get isGone(): boolean {
    return this.status === 410;
  }
}

/** Forma de erro que toda rota da API produz. */
interface DomainErrorShape {
  code: string;
  message: string;
}

/**
 * Remove os membros de erro da união inferida pelo Eden.
 *
 * O Treaty tipa `data` como a união de **todos** os corpos que a rota pode
 * devolver, incluindo os de erro (`/api/me` responde 401 com `{code, message}`,
 * por exemplo). Sem esta exclusão, o chamador receberia uma união onde o membro
 * de sucesso não é acessível sem narrowing manual em cada uso — e o valor do
 * type-safe ponta a ponta se perderia em casts.
 */
export type SuccessData<T> = T extends DomainErrorShape ? never : T;

/**
 * Formato real dos dados na rede.
 *
 * O Eden deriva os tipos do retorno dos handlers, onde as datas são `Date`.
 * Mas a resposta passa por JSON, e `Date` chega como string ISO. Sem esta
 * conversão, o compilador afirmaria `Date` para um valor que em runtime é
 * `string` — e `.getTime()` quebraria só em produção.
 *
 * `Serialized<T>` torna essa fronteira explícita no tipo, forçando a
 * hidratação antes do uso.
 */
export type Serialized<T> = T extends Date
  ? string
  : T extends (infer U)[]
    ? Serialized<U>[]
    : T extends object
      ? { [K in keyof T]: Serialized<T[K]> }
      : T;

/**
 * Normaliza a resposta do Eden.
 *
 * O Treaty devolve `{ data, error }` em vez de lançar. Converter em exceção
 * aqui permite que o TanStack Query trate erro de forma uniforme e que os
 * componentes usem `data` sem checagem de nulo em toda chamada.
 */
export function unwrap<T>(response: {
  data: T | null;
  error: unknown;
  status?: number;
}): SuccessData<T> {
  if (response.error !== null && response.error !== undefined) {
    const error = response.error as { status?: number; value?: unknown };
    const status = error.status ?? response.status ?? 500;
    const value = error.value;

    if (typeof value === "object" && value !== null && "code" in value && "message" in value) {
      throw new ApiError(status, value as ApiErrorPayload);
    }

    throw new ApiError(status, {
      code: "UNKNOWN",
      message:
        typeof value === "string" && value !== ""
          ? value
          : `A requisição falhou com status ${status}.`,
    });
  }

  if (response.data === null) {
    throw new ApiError(500, {
      code: "EMPTY",
      message: "Resposta vazia da API.",
    });
  }

  // Neste ponto o Eden já garantiu ausência de erro, então `data` é o membro de
  // sucesso da união. O compilador não consegue provar isso sozinho porque a
  // correlação entre `error` e `data` não é expressa no tipo do Treaty.
  return response.data as SuccessData<T>;
}

/**
 * Autenticação, por `fetch` direto.
 *
 * As rotas do Better Auth estão montadas sob o wildcard `/api/auth/*`, então o
 * Eden Treaty não consegue tipar `sign-in/email` nem `sign-out` — para o
 * Elysia, o caminho é opaco. Tentar acessá-las pelo Treaty resultaria em
 * indexação de tipo inválida.
 *
 * Estas duas funções são a fronteira dessa exceção: o resto da aplicação
 * continua inteiramente tipado pelo Treaty.
 */
export async function signInWithGoogle(callbackPath = "/calendario"): Promise<void> {
  const origin = window.location.origin;
  const response = await fetch(`${BASE_URL}/api/auth/sign-in/social`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      provider: "google",
      callbackURL: `${origin}${callbackPath}`,
      errorCallbackURL: `${origin}/login?error=google`,
    }),
  });

  if (!response.ok) {
    let message = "Não foi possível iniciar o login com Google.";
    if (response.status === 429) {
      message = "Muitas tentativas de acesso. Aguarde alguns minutos e tente novamente.";
    }
    throw new ApiError(response.status, { code: "AUTH_FAILED", message });
  }

  const data = (await response.json()) as { url?: string };
  if (typeof data.url !== "string" || data.url === "") {
    throw new ApiError(500, {
      code: "AUTH_FAILED",
      message: "Google não devolveu a URL de autorização.",
    });
  }

  window.location.assign(withGoogleHostedDomainChooser(data.url, TEAM_EMAIL_DOMAIN));
}

export async function fetchAuthOptions(): Promise<{ google: boolean; allowedDomain: string }> {
  const response = await fetch(`${BASE_URL}/api/public/auth-options`, {
    credentials: "include",
  });
  if (!response.ok) {
    return { google: false, allowedDomain: TEAM_EMAIL_DOMAIN };
  }
  return (await response.json()) as { google: boolean; allowedDomain: string };
}

export async function signOut(): Promise<void> {
  // Erro aqui não deve impedir a saída: o cliente limpa o cache e navega para
  // o login de qualquer forma, e a sessão expira no servidor.
  await fetch(`${BASE_URL}/api/auth/sign-out`, {
    method: "POST",
    credentials: "include",
  }).catch(() => undefined);
}

/** URL do WebSocket, derivada da origem atual. */
export function realtimeUrl(
  params: { slug?: string; token?: string; handle?: string } = {},
): string {
  const base = new URL(BASE_URL);
  base.protocol = base.protocol === "https:" ? "wss:" : "ws:";
  base.pathname = "/realtime";
  base.search = "";

  if (params.slug !== undefined) base.searchParams.set("slug", params.slug);
  if (params.token !== undefined) base.searchParams.set("token", params.token);
  if (params.handle !== undefined) base.searchParams.set("handle", params.handle);

  return base.toString();
}
